<template>
  <div>
    <!-- Currently built for OtherPage, but we can add props where needed to handle other pages. -->
    <div class="navbar" :style="{ background }">
      <div class="navbar-inner">
        <div class="left-area" v-if="showBack">
          <a href="#" class="back" @click.prevent="handleBack" :data-testid="generateTestId('header', 'button', 'back')">
            <img :src="addAssetsPublicPath('static/images/common/arrow_l_blue.svg')" />
          </a>
        </div>
        <div class="center-area" :style="{ color }" :data-testid="generateTestId('header', 'text', 'title')">{{ t(text) }}</div>
        <div class="right-area">
          <a v-if="showFAQ" href="/faq_list/" :data-testid="generateTestId('header', 'button', 'question')">
            <img :src="addAssetsPublicPath(questionIconUrl)" />
          </a>
          <a class="external" v-if="showClose" @click="closeApp()" :data-testid="generateTestId('header', 'button', 'close')">
            <img :src="addAssetsPublicPath(closeIconUrl)" />
          </a>
        </div>
      </div>
    </div>
    <div v-if="showTabBar && !isInOtherHtml" class="tabbar-wrapper">
      <TabBar
        :has-unread-notices="hasUnreadNotices"
        :is-account-opened="miniAppAssetManagementOpenFlag || false"
        :active-index="props.activeTabIndex"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { closeApp } from '@/utils/pp'
import { useI18n } from 'vue-i18n-bridge'
import { addAssetsPublicPath } from '@/utils/env.js'
import TabBar from '@/components/TabBar.vue'
import { useCommonStore } from '@/store/common'
import { useTopStore } from '@/store/top'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { generateTestId } from '@/utils/qa-test/testIds'

const isInOtherHtml = new URL(window.location.href).searchParams.get('to')
const hasUnreadNotices = ref(false)
const miniAppAssetManagementOpenFlag = ref(false)

if (!isInOtherHtml) {
  const commonStore = useCommonStore()
  const topStore = useTopStore()
  
  const commonStoreRefs = storeToRefs(commonStore)
  const topStoreRefs = storeToRefs(topStore)
  
  hasUnreadNotices.value = commonStoreRefs.hasUnreadNotices.value
  miniAppAssetManagementOpenFlag.value = !!topStoreRefs.miniAppAssetManagementOpenFlag.value
}

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    background?: string
    color?: string
    text?: string
    questionIconUrl?: string
    closeIconUrl?: string
    showBack?: boolean
    showClose?: boolean
    showFAQ?: boolean
    showBorder?: boolean
    showTabBar?: boolean
    activeTabIndex?: number
    onBack?: () => void
  }>(),
  {
    background: 'linear-gradient(180deg, #48c7eb, #51a9dd)',
    color: '#FFFFFF',
    text: 'common_pointInvestment',
    questionIconUrl: 'static/images/common/header/icon_question.svg',
    closeIconUrl: 'static/images/common/header/icon_close.svg',
    showBack: false,
    showClose: true,
    showFAQ: true,
    showBorder: false,
    showTabBar: false,
    activeTabIndex: 0,
    onBack: undefined,
  },
)

const handleBack = () => {
  props.onBack && props.onBack()
}
</script>

<style lang="scss" scoped>
@import 'src/css/mixins.scss';

.navbar {
  position: fixed;
  z-index: 500;
  height: calc(64px + env(safe-area-inset-top));

  .navbar-inner {
    border-bottom: v-bind('showBorder ? "1px solid #ccc" : "none"');
    padding-left: calc(12px + env(safe-area-inset-left));
    padding-right: calc(12px + env(safe-area-inset-right));
    padding-top: env(safe-area-inset-top);
    padding-bottom: 0;

    display: flex;
    justify-content: flex-end;
    align-items: center;
    height: 100%;

    .left-area {
      position: absolute;
      left: calc(12px + env(safe-area-inset-left));
      a {
        display: flex;
        align-items: center;
        width: 50px;
        height: 42px;
      }
    }

    .center-area {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      text-align: center;
      font-size: var(--f7-navbar-title-font-size);
      @include font-bold;
      margin-right: 1px; //minor adjustment to avoid shifting when navigating to existing navbar.
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: calc(100% - 200px);
      white-space: pre-line;
    }

    .right-area {
      margin-right: 17px;
      display: flex;
      align-items: center;
      gap: 23px;
      justify-content: flex-end;
      flex-shrink: 0;
      width: 80px;
      height: 100%;
      z-index: 1;

      a {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}

.tabbar-wrapper {
  position: fixed;
  top: calc(64px + env(safe-area-inset-top));
  z-index: 501;
  width: 100vw;
}
</style>
